import { adjustmentTypesMapping, orderTypeConstants } from "constants/order";
import { resolveOrderBenefits } from "helpers/orderBenefits";
import { parsePrice, centsToDollars } from "helpers/price";
import { addQuantityToRow } from "pages/CreateOrder/components/ServiceDetails/helpers";
import { Column } from "primereact/column";
import { useMemo, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  toggleDisplayAdjustments,
  toggleDisplayPatientResponsibility,
  toggleDisplayProviderPayoutCalculations,
} from "store/slices/serviceDetails";
import styled from "styled-components";
import { IOrder } from "types/Order/Order";
import { IProviderAmount } from "types/Payment";
import { SpecialtyList, SpecialtyListDict } from "types/Provider/Provider";

import PatientBenefits from "../PatientBenefits/PatientBenefits";
import ProviderPayoutCalculations from "../ProviderPayoutCalculations/ProviderPayoutCalculations";
import { SectionHeader, SectionTable } from "../SectionParts";

type ProviderRow = {
  code: string;
  cptDescription: string;
  amountInCents: number;
  specialty?: string;
  providerName?: string;
  quantity?: number;
};

const CardHeader = styled.div`
  border-bottom: 1 solid gray;
`;

const NcciBypassedBanner = styled.div`
  background-color: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #6d4c00;
`;

interface IServiceDetailsEdit {
  deductible: number;
  outOfPocketMax: number;
  flatCopay: number;
  coinsurancePercent: number;
  amountLeft: number;
}

function ServiceDetails({
  order,
  onUpdateOrder,
}: {
  order: IOrder;
  onUpdateOrder: () => void;
}) {
  const { setValue } = useFormContext<IServiceDetailsEdit>();
  const dispatch = useDispatch();
  const {
    displayAdjustments,
    displayPatientResponsibility,
    displayProviderPayoutCalculations,
  } = useSelector((state: any) => state.serviceDetailsSlice);

  useEffect(() => {
    // CLE-2635 — writes the same four fields as PatientBenefits.tsx into the
    // shared OrderView form; both must resolve benefits the same way.
    const benefits = resolveOrderBenefits(order);
    setValue("deductible", centsToDollars(benefits.remainingDeductibleInCents));
    setValue("outOfPocketMax", centsToDollars(benefits.outOfPocketMaxInCents));
    setValue("flatCopay", centsToDollars(benefits.flatCopayInCents));
    setValue("coinsurancePercent", benefits.coinsurancePercent);
  }, [order, setValue]);

  const columns = useMemo(() => {
    if (order.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY) {
      return [
        { field: "code", header: "CPT" },
        { field: "cptDescription", header: "Description" },
      ];
    }

    return [
      {
        field: "providerName",
        header: "Provider / Specialty",
        body: (row: ProviderRow) =>
          [row.providerName, SpecialtyListDict[row.specialty as SpecialtyList]]
            .filter(Boolean)
            .join(" · "),
        headerStyle: { width: "40%" },
      },
      {
        field: "code",
        header: "CPT / Description",
        body: (row: ProviderRow) => `${row.code} · ${row.cptDescription}`,
        headerStyle: { width: "38%" },
      },
      { field: "quantity", header: "Qty", headerStyle: { width: "8%" } },
      {
        header: "Allowable",
        field: "amountInCents",
        body: (row: ProviderRow) => {
          const quantity = row.quantity || 1;
          return parsePrice(centsToDollars(row.amountInCents * quantity));
        },
        headerStyle: { width: "14%" },
      },
    ];
  }, [order]);

  const columnsAdjustments = useMemo(() => {
    return [
      {
        field: "providerName",
        header: "Provider",
        headerStyle: { width: "40%" },
      },
      { field: "code", header: "CPT", headerStyle: { width: "12%" } },
      {
        field: "specialty",
        header: "Adjustment",
        headerStyle: { width: "32%" },
      },
      {
        field: "amountInCents",
        header: "Amount",
        body: (row: ProviderRow) =>
          parsePrice(centsToDollars(row.amountInCents)),
        headerStyle: { width: "16%" },
      },
    ];
  }, [order]);

  const columnsPatientResponsibility = useMemo(() => {
    return [
      {
        field: "providerName",
        header: "Provider",
        headerStyle: { width: "40%" },
      },
      { field: "code", header: "CPT", headerStyle: { width: "12%" } },
      {
        field: "specialty",
        header: "Responsibility",
        headerStyle: { width: "32%" },
      },
      {
        field: "amountInCents",
        header: "Amount",
        body: (row: ProviderRow) =>
          parsePrice(centsToDollars(row.amountInCents)),
        headerStyle: { width: "16%" },
      },
    ];
  }, []);

  const parseDataToPatientResponsibility = useMemo(() => {
    return (
      order.gfeQuote?.pricingBreakdown.procedureBreakdown?.map(
        (aPatientResponsibility) => {
          const { code, owedByPatient, providerName } = aPatientResponsibility;
          const { coinsurance, deductible, total, selfPay } = owedByPatient;

          let description = "";

          if (coinsurance > 0 && deductible > 0) {
            description = "Deductible + Coinsurance";
          } else if (coinsurance > 0) {
            description = "Coinsurance";
          } else if (deductible > 0) {
            description = "Deductible";
          } else if (selfPay > 0) {
            description = "Self-Pay";
          }
          return {
            code,
            amountInCents: total,
            specialty: description,
            providerName,
          };
        }
      ) || []
    );
  }, [order.gfeQuote]);

  const parseDataToAdjustment = useMemo(() => {
    return (
      order.gfeQuote?.pricingBreakdown.adjustments?.map(
        (aPatientResponsibility) => {
          const {
            procedure,
            discountType,
            discountedPrice,
            hierarchyOverridden,
            discountAmountInCents,
          } = aPatientResponsibility;
          const label = adjustmentTypesMapping[discountType] || discountType;
          // Claim-level per-provider adjustment (e.g. CHARGE_BASED_PRICING_CAP) has no procedure.
          // Show the cap (provider's post-cap subtotal) — same convention as per-procedure rows
          // which show discountedPrice. Backend carries the provider's name in the audit row.
          if (!procedure) {
            return {
              code: "",
              amountInCents:
                aPatientResponsibility.maxAmountInCents ??
                discountAmountInCents ??
                0,
              specialty: label,
              providerName:
                aPatientResponsibility.providerName || "Provider Total",
            };
          }
          const { code, providerName } = procedure;

          return {
            code,
            amountInCents: discountedPrice,
            specialty: hierarchyOverridden ? `${label} (Overridden)` : label,
            providerName,
          };
        }
      ) || []
    ).sort((a, b) => {
      const aKey = `${a.providerName}-${a.code}`;
      const bKey = `${b.providerName}-${b.code}`;
      return aKey.localeCompare(bKey);
    });
  }, [order.gfeQuote]);

  const paymentBreakdownDetails = useMemo(() => {
    const list = order?.paymentFullBreakdown?.bundledProviderAmounts;

    const sortedList = list?.length
      ? [...list].sort((a, b) => b.providerPriority - a.providerPriority)
      : [];

    // Transform IProviderAmount to ProviderAmountRow
    return sortedList.map((item: IProviderAmount) => ({
      ...item,
      patientPortion: item.patientPortion || 0, // Provide a default value or calculate
      remainingToPayProvider: item.remainingToPayProvider || 0, // Provide a default value or calculate
    }));
  }, [order]);

  const clearFee = useMemo(() => {
    return order?.paymentFullBreakdown?.clearFeeAmountInCents;
  }, [order]);

  const payments = order.paymentPlanBreakdown
    ? order.paymentPlanBreakdown.payments
    : undefined;

  // Extract splitAmountsByProvider from both breakdowns
  const paymentPlanSplitAmountsByProvider = useMemo(() => {
    return (order.paymentPlanBreakdown as any)?.splitAmountsByProvider;
  }, [order.paymentPlanBreakdown]);

  const fullBreakdownSplitAmountsByProvider = useMemo(() => {
    return (order.paymentFullBreakdown as any)?.splitAmountsByProvider;
  }, [order.paymentFullBreakdown]);

  const orderTypeRules = useMemo(() => {
    const isGFEorPatientResponsibility =
      order.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY ||
      order.orderType === orderTypeConstants.GFE;
    const isPatientResponsibility =
      order.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY;
    const isGFE = order.orderType === orderTypeConstants.GFE;

    return { isGFEorPatientResponsibility, isPatientResponsibility, isGFE };
  }, [order.orderType]);

  return (
    <>
      {orderTypeRules.isGFE && <PatientBenefits order={order} />}
      <section
        className="px-3 pb-3 border-bottom-1"
        style={{
          borderColor: "var(--brand-gray-200)",
          marginLeft: "-1rem",
          marginRight: "-1rem",
        }}
      >
        <SectionHeader icon="pi pi-list" title="Provider Calculations" />
        <SectionTable
          className="mt-3 border-1 border-round-2xl overflow-hidden"
          style={{ borderColor: "var(--brand-gray-200)" }}
          header="Service Details"
          value={
            orderTypeRules.isPatientResponsibility
              ? order.serviceDetails.procedures
              : addQuantityToRow(order.serviceDetails.procedures)
          }
        >
          {columns.map((col: any) => (
            <Column
              key={col.field}
              field={col.field}
              header={col.header}
              body={col?.body}
              headerStyle={col.headerStyle}
            />
          ))}
        </SectionTable>
        {orderTypeRules.isGFE && (
          <>
            <div>
              <div
                className="mt-3 border-1 border-round-2xl overflow-hidden"
                style={{ borderColor: "var(--brand-gray-200)" }}
              >
                <CardHeader
                  className="flex align-items-center justify-content-between font-bold cursor-pointer"
                  style={{
                    background: "var(--brand-gray-50)",
                    color: "var(--brand-black)",
                    fontSize: "0.9375rem",
                    padding: "0.875rem 1rem",
                  }}
                  onClick={() => {
                    dispatch(toggleDisplayAdjustments());
                  }}
                >
                  Adjustments
                  <i
                    className={
                      displayAdjustments ? "pi pi-angle-up" : "pi pi-angle-down"
                    }
                    style={{ color: "var(--brand-gray-600)" }}
                  />
                </CardHeader>
                {displayAdjustments && (
                  <SectionTable
                    className="border-top-1"
                    style={{ borderColor: "var(--brand-gray-200)" }}
                    emptyMessage="No adjustments"
                    value={parseDataToAdjustment}
                  >
                    {columnsAdjustments.map((col) => (
                      <Column
                        key={col.field}
                        field={col.field}
                        body={col?.body}
                        header={col?.header}
                        headerStyle={col.headerStyle}
                      />
                    ))}
                  </SectionTable>
                )}
              </div>
              {displayAdjustments &&
                (order.gfeQuote as any)?.excludedNcciRulesetIds?.length > 0 && (
                  <NcciBypassedBanner>
                    <i
                      className="pi pi-info-circle"
                      style={{ marginRight: "0.4rem" }}
                    />
                    <strong>NCCI Adjustments Bypassed</strong>
                    <span style={{ marginLeft: "0.5rem" }}>
                      {(order.gfeQuote as any).excludedNcciRulesetIds.length}{" "}
                      NCCI rule
                      {(order.gfeQuote as any).excludedNcciRulesetIds.length > 1
                        ? "s were"
                        : " was"}{" "}
                      bypassed when this estimate was created. The adjustments
                      above reflect the recalculated pricing.
                    </span>
                  </NcciBypassedBanner>
                )}
            </div>
            <div
              className="mt-3 border-1 border-round-2xl overflow-hidden"
              style={{ borderColor: "var(--brand-gray-200)" }}
            >
              <CardHeader
                className="flex align-items-center justify-content-between font-bold cursor-pointer"
                style={{
                  background: "var(--brand-gray-50)",
                  color: "var(--brand-black)",
                  fontSize: "0.9375rem",
                  padding: "0.875rem 1rem",
                }}
                onClick={() => {
                  dispatch(toggleDisplayPatientResponsibility());
                }}
              >
                Patient Responsibility
                <i
                  className={
                    displayPatientResponsibility
                      ? "pi pi-angle-up"
                      : "pi pi-angle-down"
                  }
                  style={{ color: "var(--brand-gray-600)" }}
                />
              </CardHeader>
              {displayPatientResponsibility && (
                <SectionTable
                  className="border-top-1"
                  style={{ borderColor: "var(--brand-gray-200)" }}
                  emptyMessage="No Patient Balance"
                  value={parseDataToPatientResponsibility}
                >
                  {columnsPatientResponsibility.map((col) => (
                    <Column
                      key={col.field}
                      field={col.field}
                      body={col?.body}
                      header={col?.header}
                      headerStyle={col.headerStyle}
                    />
                  ))}
                </SectionTable>
              )}
            </div>
          </>
        )}
        {orderTypeRules.isGFEorPatientResponsibility && (
          <div>
            <div
              className="mt-3 border-1 border-round-2xl overflow-hidden"
              style={{ borderColor: "var(--brand-gray-200)" }}
            >
              <CardHeader
                className="flex align-items-center justify-content-between font-bold cursor-pointer"
                style={{
                  background: "var(--brand-gray-50)",
                  color: "var(--brand-black)",
                  fontSize: "0.9375rem",
                  padding: "0.875rem 1rem",
                }}
                onClick={() => {
                  dispatch(toggleDisplayProviderPayoutCalculations());
                }}
              >
                Provider Payout Calculations
                <i
                  className={
                    displayProviderPayoutCalculations
                      ? "pi pi-angle-up"
                      : "pi pi-angle-down"
                  }
                  style={{ color: "var(--brand-gray-600)" }}
                />
              </CardHeader>
            </div>
            {displayProviderPayoutCalculations && (
              <ProviderPayoutCalculations
                providerAmounts={paymentBreakdownDetails}
                clearFee={clearFee}
                payments={payments}
                fullBreakdownSplitPaymentDetails={
                  order?.paymentFullBreakdown?.splitPaymentDetails
                }
                paymentPlanSplitPaymentDetails={
                  order?.paymentPlanBreakdown?.splitPaymentDetails
                }
                orderStatus={order.status}
                orderId={order.id}
                onUpdateOrder={onUpdateOrder}
                clearPatientFeeValueInPercentage={
                  order.clearPatientFeeValueInPercentage
                }
                paymentPlanSplitAmountsByProvider={
                  paymentPlanSplitAmountsByProvider
                }
                fullBreakdownSplitAmountsByProvider={
                  fullBreakdownSplitAmountsByProvider
                }
                orderType={order.orderType}
                paymentPlan={order?.paymentPlan?.find((p) => p.active)}
              />
            )}
          </div>
        )}
      </section>
    </>
  );
}

export default ServiceDetails;
